import { Injectable, Injector, EventEmitter, inject, DOCUMENT } from '@angular/core';
import { Router } from '@angular/router';
import { CookieOptions, CookieService } from 'ngx-cookie';
import { MessageService } from 'primeng/api';
import { AppProperty } from '../properties/app.property';
import { CompanyProperty } from '../properties/company.property';
import { AppService } from '../services/app.service';

import { UserProperty } from '../properties/user.property';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators'
import { Observable } from 'rxjs';

import * as CryptoJS from 'crypto-js';
import { environment } from '../../environments/environment';
import { apps } from '../constants/app-constant';
import { OAuthProviderStatus, OAuthAuthorizeResponse } from '../models/oauth.class';
import { MsalService } from '@azure/msal-angular';
import { clearAuthSessionFlags } from '@neworg-net/amsuite-core/auth';
import { ConfigService } from '@neworg-net/amsuite-core/config';
import { normalizePrimeIconClass } from './prime-icon.util';
import { STOP_NOTIFICATION_SESSION } from '../services/notifications/notification-session-control';
import { LoginReturnService } from '../services/login-return.service';


/**
 * Dominio dei cookie di auth: `localhost` in sviluppo, `.neworg.net` (cross-subdomain)
 * in deploy. Confronto sull'hostname intero e non `indexOf('localhost') >= 0`:
 * un host come `localhost.attacker.test` conteneva la sottostringa senza essere locale.
 */
function authCookieDomain(): string {
  const hostname = location.hostname.toLowerCase();
  const isLocal = hostname === 'localhost' || hostname.endsWith('.localhost');
  return isLocal ? 'localhost' : '.neworg.net';
}


/**
 * Costruisce il deep-link verso una rotta interna di un'app ospitata.
 *
 * Tutte le app della suite usano hash routing (`useHash: true`): un URL senza `#` fa servire
 * `index.html` e l'app parte sulla propria rotta `''`, quindi il widget Dialog finiva sulla
 * dashboard di Dialog invece che sul dettaglio della richiesta.
 *
 * L'hash viene aggiunto solo se l'URL del catalogo non lo porta gia', perche' gli ambienti lo
 * registrano nelle due forme; e gli slash finali vengono tolti, altrimenti un `url` che finisce
 * con `/` produce `host//dialog/<id>`.
 */
export function buildAppDeepLink(appUrl: string, path: unknown, id: unknown): string {
  const base = String(appUrl ?? '').replace(/\/+$/, '');
  const tail = [path, id]
    .filter(part => part != null && String(part).trim() !== '')
    .map(part => String(part).replace(/^\/+|\/+$/g, ''))
    .join('/');

  return base.includes('#') ? `${base}/${tail}` : `${base}/#/${tail}`;
}


@Injectable()
export class AppStateHolder {
  private _companyProperties = inject(CompanyProperty);
  private _appProperty = inject(AppProperty);
  private _appService = inject(AppService);
  private _cookieService = inject(CookieService);
  private http = inject(HttpClient);
  private _messageService = inject(MessageService);
  private _userProperty = inject(UserProperty);
  private _document = inject(DOCUMENT);
  private _router = inject(Router);
  private _msalService = inject(MsalService);
  private readonly config = inject(ConfigService);
  private readonly injector = inject(Injector);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() { }

  cId: any;
  companies: any;
  types: any[];
  holdingApps = [];
  cmpApp: any;
  activityParams = [];
  projectParams = [];
  stateGraphParams = [];
  hourParams = [];
  rtypes = [];
  typologyGraphParams = [];

  showAuthLoginModal: boolean = false;

  activityId: string;
  appSelected: EventEmitter<any> = new EventEmitter();
  appNotFound: EventEmitter<string> = new EventEmitter();

  // OAuth state
  oauthState = {
    providers: [] as OAuthProviderStatus[],
    authorizeResponse: null as OAuthAuthorizeResponse
  };


  loginWindowIsOpen: boolean = false;
  loginWindow: any;

  //#region App

  /**
   * Get app model from id
   * @param id
   * @returns Set app into appSelected
   */
  getApp(id: string): void {
    const app = this._companyProperties.userCompanyApps.find(a => a.id == id.toLowerCase());
    if (app !== undefined) {
      if (document.location.host.indexOf("demo.") >= 0 && app.url.indexOf("demo.") < 0) {
        app.url = app.url.replace('.neworg.net', 'demo.neworg.net');
      }
      this.appSelected.next(app);
      return;
    }

    this.appNotFound.next(id);
  }

  /**
   * Get app url from app id
   * @param id
   * @returns App url
   */
  getAppUrl(id: string): string {
    const app = this._companyProperties.userCompanyApps.find(a => {
      return a.id.toLowerCase() == id.toLowerCase()
    })

    if (app !== undefined) {
      this.appSelected.next(app);

      return app.url;
    }

    return null;
  }

  //#region Style class

  /**
   * Set app Navbar class from app params
   * @param id
   */
  setAppNavbarClass(idApp: any) {
    if (idApp === 'setHREmergencyNavbarClass') {
      this.setHREmergencyNavbarClass();
      return;
    }

    const normalizedId = String(idApp ?? '').toLowerCase();
    const app = this._companyProperties.userCompanyApps.find(
      candidate => String(candidate?.id ?? '').toLowerCase() === normalizedId,
    );

    if (!app) {
      this.appNotFound.next(String(idApp ?? ''));
      return;
    }

    this._appProperty.setAppClass(app.cssParams?.appClass ?? 'app-orange');
    this._appProperty.setAppName('AmSuite ' + app.functionName);
  }

  /**
   * Rotte che ospitano un'app esterna nell'iframe: `/app/:id`, `/:appid/app/:id` e i
   * deep-link `/comunica/**`. Sono le uniche che devono portare l'identita' cromatica
   * dell'app ospitata; ogni altra rotta e' shell AmSuite.
   */
  isHostedAppRoute(url: string): boolean {
    const segments = (url || '').split(/[?#]/)[0].split('/').filter(Boolean);
    return segments[0] === 'comunica' || segments[0] === 'app' || segments[1] === 'app';
  }

  /**
   * Riporta l'header all'identita' della shell AmSuite.
   * `setAppNavbarClass` e' a senso unico: colora l'header entrando in un'app ospitata,
   * ma nessuno lo ripristinava uscendone, e il back del browser non passa da
   * `onSelectApp`. Tornando in dashboard dal dettaglio aperto da un widget l'header
   * restava del colore dell'app appena lasciata.
   */
  resetAppNavbarClass() {
    this._appProperty.setAppClass('app-orange');
    this._appProperty.setAppName('AmSuite');
  }

  // Legacy navbar mapping retained for persisted refunded-cost shortcuts.
  setRefundedCostsNavbarClass() {
    this._appProperty.setAppClass('app-green');
    this._appProperty.setAppName('AmSuite Nota spese');
  }

  setHREmergencyNavbarClass() {
    this._appProperty.setAppClass('app-green');
    this._appProperty.setAppName('AmSuite Lista di emergenza');
  }

  /**
   * Get icon class for sidebar
   * @param icon
   * @returns a PrimeIcons class, including compatibility for persisted legacy values
   */
  iconClass(icon: any) {
    return normalizePrimeIconClass(icon);
  }

  //#endregion

  //#endregion


  isAuthenticated(): boolean {
    return this._msalService.instance.getAllAccounts().length > 0;
  }


  signOut(): void {
    this.injector.get(STOP_NOTIFICATION_SESSION)();
    this.removeCookie('authType');
    this.removeCookie('authToken');
    this.removeCookie('authTypeDomain');
    this.removeCookie('ImpersonateState');
    this.removeCookie('ImpersonationSessionId');
    // Senza questo i rilogin successivi vedono il flag stantio, skippano
    // `bootstrapBackendCookie()` e le satellite (calendar in iframe, ecc.)
    // ricevono 401 finche` non scatta la recovery automatica.
    clearAuthSessionFlags();
    this.injector.get(LoginReturnService).clear();

    this._appService.signOut().subscribe({
      complete: () => {
        this._msalService.logoutRedirect({ postLogoutRedirectUri: '/login' });
      },
      error: () => {
        this._msalService.logoutRedirect({ postLogoutRedirectUri: '/login' });
      }
    });
  }

  //#region Crypting

  encryptValue(value: string): string {
    const key = CryptoJS.enc.Utf8.parse(this.config.cryptingKey);
    const encrypted = CryptoJS.AES.encrypt(value, key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    });
    return encrypted.toString();
  }


  decryptValue(value: string): string {
    const key = CryptoJS.enc.Utf8.parse(this.config.cryptingKey);
    const decrypted = CryptoJS.AES.decrypt(value, key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  //#endregion

  //#region Cookies


  /**
* @param {string} key Id to use for lookup.
* @returns {string} Raw cookie value.
*/
  getCookie(key: string) {
    const cookie = this._cookieService.get(key);

    try {
      const decryptedValue = this.decryptValue(cookie);
      const result = decryptedValue && decryptedValue.trim().length > 0 ? decryptedValue : cookie;
      return result;

    } catch (e) {
      return cookie;
    }
  }

  /**
   * @param {string} key Id for the `value`.
   * @param {string} value Raw value to be stored.
   */
  setCookie(key: string, value: string): void {
    const domain = authCookieDomain()
    const expiryDate = new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365); // 1 year

    const opt: CookieOptions = {
      domain: domain,
      expires: expiryDate,
    };

    try {
      const encryptedValue = this.encryptValue(value);
      return this._cookieService.put(key, encryptedValue, opt);

    } catch (e) {
      return this._cookieService.put(key, value, opt);
    }
  }

  /**
  * @param {string} key Id of the key-value pair to delete.
  * @param {CookieOptions} options (Optional) Options object.
  */
  removeCookie(key: string): void {
    const domain = authCookieDomain()

    const opt: CookieOptions = {
      domain: domain
    };

    return this._cookieService.remove(key, opt);
  }


  //#endregion

  //#region JWT Token
  validateJWT(token: any): boolean {
    return this.isValidFormat(token) && this.isValidLength(token) && this.isValidContent(token);
  }


  isValidFormat(token: string): boolean {
    return token.split('.').length === 3;
  }


  isValidLength(token: string, minLength: number = 10): boolean {
    return token.split('.').every(part => part.length >= minLength);
  }

  isValidContent(token: string): boolean {
    try {
      const parts = token.split('.');
      const header = JSON.parse(atob(parts[0]));
      const payload = JSON.parse(atob(parts[1]));
      // Qui puoi aggiungere ulteriori controlli sul contenuto del header e del payload se necessario
      return true;
    } catch (error) {
      return false; // Qualcosa non va nei dati decodificati
    }
  }

  //#endregion

  navigateToAppFromEvent(navigationParams: string) {
    const params = JSON.parse(navigationParams);
    this.setAppNavbarClass(params.appId);
    const appUrl = this.getAppUrl(params.appId);

    // App sconosciuta: getAppUrl ha gia' segnalato via appNotFound. Interpolare il null
    // portava a /app/null%2F..., una rotta valida che apriva un iframe verso il nulla.
    if (!appUrl) {
      return;
    }

    this._router.navigate(['/app', buildAppDeepLink(appUrl, params.path, params.id)]);
  }

  clearToasts() {
    this._messageService.clear();
  }

  mapDevURL(companies: any) {
    const hostname = this._document.location.hostname.toLowerCase();
    // Attiva rimappatura in deploy DEV o in sviluppo locale. Suffisso e non
    // `indexOf`: `-dev`/`localhost` in mezzo a un host arbitrario non identificano
    // l'ambiente (es. `localhost.attacker.test`, `x-dev.altro.test`).
    const isDevDeploy = hostname.endsWith('-dev.neworg.net');
    const isLocal = hostname === 'localhost' || hostname.endsWith('.localhost');
    if (isDevDeploy || isLocal) {
      const mappings = environment.devUrlMappings || [];
      companies.forEach(company => {
        company.apps.forEach(app => {
          for (const mapping of mappings) {
            if (app.url.includes(mapping.from)) {
              app.url = app.url.replace(mapping.from, mapping.to);
              break;  // preserva semantica else-if originale (solo primo match)
            }
          }
        });
      });
    }
  }
}
